import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import { useTranslation } from "../../../utils/translation";
import { useLocation } from "react-router-dom";
import {
  createUserByAdmin,
  listUsers,
  setUserStandard,
  updateUserById,
  selectUsersCreateError,
  selectUsersCreateLoading,
  selectUsersCreateMessage,
  selectUsers,
  selectUsersError,
  selectUsersLoading,
  selectUsersMessage,
} from "../userSlice";

const PAGE_SIZE = 10;

const UsersListPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const users = useSelector(selectUsers);
  const isLoading = useSelector(selectUsersLoading);
  const isError = useSelector(selectUsersError);
  const message = useSelector(selectUsersMessage);
  const createLoading = useSelector(selectUsersCreateLoading);
  const createError = useSelector(selectUsersCreateError);
  const createMessage = useSelector(selectUsersCreateMessage);
  const updateLoading = useSelector((state) => state.user?.updateLoading);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [packageFilter, setPackageFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDetailEditMode, setIsDetailEditMode] = useState(false);
  const [isDemoEditMode, setIsDemoEditMode] = useState(false);
  const [isPremiumEditMode, setIsPremiumEditMode] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [demoForm, setDemoForm] = useState({
    demostartdate: "",
    demoenddate: "",
    demoduration: "",
    demototalanalysisminutes: "",
    demoremaininganalysisminutes: "",
    demopackage: "",
  });
  const [createSuccessMessage, setCreateSuccessMessage] = useState("");
  const [premiumForm, setPremiumForm] = useState({
    premiumstartdate: "",
    premiumenddate: "",
    premiumduration: "",
    premiumtotalanalysisminutes: "",
    premiumremaininganalysisminutes: "",
    premiumpackage: "",
  });
  const [detailForm, setDetailForm] = useState({
    name: "",
    surName: "",
    userEmail: "",
    phoneNumber: "",
    userName: "",
    role: "",
    businessId: "",
    businessName: "",
    businessPosition: "",
    job: "",
    city: "",
    location: "",
    country: "",
    language: "",
    about: "",
    state: false,
    premium: false,
  });
  const [createForm, setCreateForm] = useState({
    name: "",
    surName: "",
    email: "",
    phoneNumber: "",
    tempPassword: "",
    userName: "",
    businessName: "",
    about: "",
    businessPosition: "",
    job: "",
    city: "",
    location: "",
    country: "",
    language: "",
    businessId: "",
  });

  useEffect(() => {
    dispatch(listUsers());
  }, [dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const packageType = (params.get("package") || "").toLowerCase();
    if (packageType === "premium" || packageType === "standard") {
      setPackageFilter(packageType);
    } else {
      setPackageFilter("all");
    }
    setPage(1);
  }, [location.search]);

  const getFullName = (user) => {
    if (user?.fullName) return user.fullName;
    const nameParts = [user?.name, user?.surName].filter(Boolean);
    return nameParts.length ? nameParts.join(" ") : "";
  };

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>
          {match}
        </span>
        {after}
      </>
    );
  };

  const getEmail = (user) => {
    return user?.email || user?.userEmail || "-";
  };

  const getRole = (user) => {
    return user?.role || user?.userType || "-";
  };

  const isPremiumUser = (user) => Boolean(user?.premium);
  const isDemoUser = (user) => user?.demo === true;
  const isPremiumOnlyUser = (user) => user?.premium === true && user?.demo !== true;

  const formatDate = (value) => {
    if (!value) return "-";
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleString("tr-TR");
    }
    if (typeof value === "number") {
      return new Date(value).toLocaleString("tr-TR");
    }
    if (typeof value === "string") return value;
    return "-";
  };

  const toDateValue = (value) => {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    if (typeof value === "number") return new Date(value);
    if (typeof value === "string") {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const isDemoExpired = (user) => {
    const endDate = toDateValue(user?.demoenddate);
    if (!endDate) return false;
    return endDate.getTime() < Date.now();
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    const searched = !term ? (users || []) : (users || []).filter((user) => {
      const name = getFullName(user).toLowerCase();
      const email = (user?.email || user?.userEmail || "").toLowerCase();
      return name.includes(term) || email.includes(term);
    });
    if (packageFilter === "premium") {
      return searched.filter((user) => isPremiumOnlyUser(user));
    }
    if (packageFilter === "demo") {
      return searched.filter((user) => isDemoUser(user));
    }
    if (packageFilter === "standard") {
      return searched.filter((user) => !isPremiumUser(user));
    }
    return searched;
  }, [users, search, packageFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const buildMailStatusSuffix = (result) => {
    const status = result?.mailStatus;
    if (status === "sent") return " Kullanıcıya mail gönderildi.";
    if (status === "failed") return " Kullanıcı oluşturuldu, ancak mail gönderilemedi.";
    if (status === "skipped") return " Kullanıcı oluşturuldu, ancak e-posta olmadığı için mail gönderilmedi.";
    return "";
  };

  const valueOrDash = (value) => {
    if (value === null || value === undefined) return "-";
    const text = String(value).trim();
    return text ? text : "-";
  };

  const getDetailFormFromUser = (user) => ({
    name: user?.name || "",
    surName: user?.surName || "",
    userEmail: user?.userEmail || user?.email || "",
    phoneNumber: user?.phoneNumber || "",
    userName: user?.userName || "",
    role: user?.role || user?.userType || "",
    businessId: user?.businessId || "",
    businessName: user?.businessName || "",
    businessPosition: user?.businessPosition || "",
    job: user?.job || "",
    city: user?.city || "",
    location: user?.location || "",
    country: user?.country || "",
    language: user?.language || "",
    about: user?.about || "",
    state: Boolean(user?.state),
    premium: Boolean(user?.premium),
  });

  const openDetailModal = (user) => {
    setSelectedUser(user);
    setDetailForm(getDetailFormFromUser(user));
    setDetailError("");
    setIsDetailEditMode(false);
    setIsDemoEditMode(false);
    setIsPremiumEditMode(false);
    setDemoForm({
      demostartdate: user?.demostartdate || "",
      demoenddate: user?.demoenddate || "",
      demoduration: user?.demoduration || "",
      demototalanalysisminutes: user?.demototalanalysisminutes || "",
      demoremaininganalysisminutes: user?.demoremaininganalysisminutes || "",
      demopackage: user?.demopackage || "",
    });
    setPremiumForm({
      premiumstartdate: user?.premiumstartdate || "",
      premiumenddate: user?.premiumenddate || "",
      premiumduration: user?.premiumduration || "",
      premiumtotalanalysisminutes: user?.premiumtotalanalysisminutes || "",
      premiumremaininganalysisminutes: user?.premiumremaininganalysisminutes || "",
      premiumpackage: user?.premiumpackage || "",
    });
  };

  const handleDetailChange = (event) => {
    const { name, value } = event.target;
    setDetailForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDetailSave = async () => {
    if (!adminAccess) {
      setDetailError("Bu işlem için yetkiniz yok.");
      return;
    }
    if (!selectedUser?.id && !selectedUser?.userId) {
      setDetailError("Kullanıcı kimliği bulunamadı.");
      return;
    }
    if (!detailForm.name.trim() || !detailForm.surName.trim()) {
      setDetailError("Ad ve Soyad zorunludur.");
      return;
    }
    const id = selectedUser?.id || selectedUser?.userId;
    const payload = {
      name: detailForm.name.trim(),
      surName: detailForm.surName.trim(),
      userEmail: detailForm.userEmail.trim().toLowerCase(),
      phoneNumber: detailForm.phoneNumber.trim(),
      userName: detailForm.userName.trim(),
      role: detailForm.role.trim(),
      businessId: detailForm.businessId.trim(),
      businessName: detailForm.businessName.trim(),
      businessPosition: detailForm.businessPosition.trim(),
      job: detailForm.job.trim(),
      city: detailForm.city.trim(),
      location: detailForm.location.trim(),
      country: detailForm.country.trim(),
      language: detailForm.language.trim(),
      about: detailForm.about.trim(),
      state: detailForm.state,
      premium: detailForm.premium,
    };

    try {
      const updated = await dispatch(updateUserById({ id, data: payload })).unwrap();
      await dispatch(listUsers());
      setSelectedUser(updated);
      setDetailForm(getDetailFormFromUser(updated));
      setIsDetailEditMode(false);
      setDetailError("");
    } catch (error) {
      setDetailError(error?.message || "Kullanıcı güncellenemedi.");
    }
  };

  const handleDemoSave = async () => {
    const id = selectedUser?.id || selectedUser?.userId;
    if (!id) {
      setDetailError("Kullanıcı kimliği bulunamadı.");
      return;
    }
    if (!demoForm.demostartdate || !demoForm.demoenddate) {
      setDetailError("Demo başlangıç ve bitiş tarihi zorunludur.");
      return;
    }
    if (new Date(demoForm.demostartdate) > new Date(demoForm.demoenddate)) {
      setDetailError("Demo başlangıç tarihi bitiş tarihinden büyük olamaz.");
      return;
    }
    try {
      const updated = await dispatch(updateUserById({
        id,
        data: {
          demostartdate: demoForm.demostartdate,
          demoenddate: demoForm.demoenddate,
          demoduration: demoForm.demoduration,
          demototalanalysisminutes: demoForm.demototalanalysisminutes,
          demoremaininganalysisminutes: demoForm.demoremaininganalysisminutes,
          demopackage: demoForm.demopackage,
          premium: true,
          demo: true,
        },
      })).unwrap();
      await dispatch(listUsers());
      setSelectedUser(updated);
      setIsDemoEditMode(false);
      setDetailError("");
    } catch (error) {
      setDetailError(error?.message || "Demo tanımlanamadı.");
    }
  };

  const handleToggleUserActive = async (event, user) => {
    event.stopPropagation();
    const id = user?.id || user?.userId;
    if (!id) return;
    try {
      const nextState = !Boolean(user?.state);
      await dispatch(updateUserById({ id, data: { state: nextState } })).unwrap();
    } catch (error) {
      swal("Hata", error?.message || "Kullanıcı durumu güncellenemedi.", "error");
    }
  };

  const handlePremiumSave = async () => {
    const id = selectedUser?.id || selectedUser?.userId;
    if (!id) {
      setDetailError("Kullanıcı kimliği bulunamadı.");
      return;
    }
    if (!premiumForm.premiumstartdate || !premiumForm.premiumenddate) {
      setDetailError("Premium başlangıç ve bitiş tarihi zorunludur.");
      return;
    }
    if (new Date(premiumForm.premiumstartdate) > new Date(premiumForm.premiumenddate)) {
      setDetailError("Premium başlangıç tarihi bitiş tarihinden büyük olamaz.");
      return;
    }
    const premiumDuration = Number.parseInt(premiumForm.premiumduration, 10);
    const premiumTotalAnalysisMinutes = Number.parseInt(premiumForm.premiumtotalanalysisminutes, 10);
    const premiumRemainingAnalysisMinutes = Number.parseInt(premiumForm.premiumremaininganalysisminutes, 10);
    try {
      const updated = await dispatch(updateUserById({
        id,
        data: {
          demo: false,
          demopackage: "",
          demoduration: 0,
          demoenddate: null,
          demoremaininganalysisminutes: 0,
          demostartdate: null,
          demototalanalysisminutes: 0,
          premium: true,
          premiumpackage: premiumForm.premiumpackage,
          premiumduration: Number.isNaN(premiumDuration) ? 0 : premiumDuration,
          premiumenddate: premiumForm.premiumenddate,
          premiumremaininganalysisminutes: Number.isNaN(premiumRemainingAnalysisMinutes) ? 0 : premiumRemainingAnalysisMinutes,
          premiumstartdate: premiumForm.premiumstartdate,
          premiumtotalanalysisminutes: Number.isNaN(premiumTotalAnalysisMinutes) ? 0 : premiumTotalAnalysisMinutes,
        },
      })).unwrap();
      await dispatch(listUsers());
      setSelectedUser(updated);
      setIsPremiumEditMode(false);
      setDetailError("");
    } catch (error) {
      setDetailError(error?.message || "Premium tanımlanamadı.");
    }
  };

  const handleEndDemo = async () => {
    const id = selectedUser?.id || selectedUser?.userId;
    if (!id) {
      setDetailError("Kullanıcı kimliği bulunamadı.");
      return;
    }
    try {
      const updated = await dispatch(setUserStandard({ id })).unwrap();
      await dispatch(listUsers());
      setSelectedUser(updated);
      setDetailError("");
      setIsDemoEditMode(false);
    } catch (error) {
      setDetailError(error?.message || "Demo bitirilemedi.");
    }
  };

  const handleEndPremium = async () => {
    const id = selectedUser?.id || selectedUser?.userId;
    if (!id) {
      setDetailError("Kullanıcı kimliği bulunamadı.");
      return;
    }
    try {
      const updated = await dispatch(updateUserById({
        id,
        data: {
          premium: false,
          premiumpackage: "",
          premiumduration: 0,
          premiumenddate: null,
          premiumremaininganalysisminutes: 0,
          premiumstartdate: null,
          premiumtotalanalysisminutes: 0,
        },
      })).unwrap();
      await dispatch(listUsers());
      setSelectedUser(updated);
      setDetailError("");
      setIsPremiumEditMode(false);
    } catch (error) {
      setDetailError(error?.message || "Premium bitirilemedi.");
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      name: "",
      surName: "",
      email: "",
      phoneNumber: "",
      tempPassword: "",
      userName: "",
      businessName: "",
      about: "",
      businessPosition: "",
      job: "",
      city: "",
      location: "",
      country: "",
      language: "",
      businessId: "",
    });
  };

  const handleCreateChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateUser = async () => {
    if (!adminAccess) {
      swal("Yetki Hatası", "Bu işlem için yetkiniz yok.", "error");
      return;
    }
    const payload = {
      name: createForm.name?.trim() || "",
      surName: createForm.surName?.trim() || "",
      email: createForm.email?.trim() || "",
      phoneNumber: createForm.phoneNumber?.trim() || "",
      tempPassword: createForm.tempPassword?.trim() || "",
      userName: createForm.userName?.trim() || "",
      businessName: createForm.businessName?.trim() || "",
      about: createForm.about?.trim() || "",
      businessPosition: createForm.businessPosition?.trim() || "",
      job: createForm.job?.trim() || "",
      city: createForm.city?.trim() || "",
      location: createForm.location?.trim() || "",
      country: createForm.country?.trim() || "",
      language: createForm.language?.trim() || "",
      businessId: createForm.businessId?.trim() || "",
    };

    if (!payload.name || !payload.surName || !payload.email || !payload.tempPassword) {
      swal("Eksik Alan", "Ad, Soyad, E-posta ve Geçici Şifre zorunludur.", "error");
      return;
    }
    if (payload.tempPassword.length < 6) {
      swal("Eksik Alan", "Geçici şifre en az 6 karakter olmalıdır.", "error");
      return;
    }

    try {
      const result = await dispatch(createUserByAdmin(payload)).unwrap();
      await dispatch(listUsers());
      setCreateSuccessMessage(`Kullanıcı başarıyla oluşturuldu.${buildMailStatusSuffix(result)}`);
      resetCreateForm();
      setShowCreateModal(false);
    } catch (error) {
      // Error state handled by slice selectors.
    }
  };

  const renderRows = () => {
    if (isLoading && (!users || users.length === 0)) {
      return (
        <tr>
          <td colSpan="6">{t("admin_common.loading")}</td>
        </tr>
      );
    }
    if (isError && (!users || users.length === 0)) {
      return (
        <tr>
          <td colSpan="6" className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }
    if (!pagedUsers || pagedUsers.length === 0) {
      return (
        <tr>
          <td colSpan="6">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    return pagedUsers.map((user) => {
      const fullName = getFullName(user);
      const isPremium = isPremiumUser(user);
      const isDemo = isDemoUser(user);
      const isPremiumOnly = isPremiumOnlyUser(user);
      return (
        <tr
          key={user.id || user.userId}
          style={{ cursor: "pointer" }}
          onClick={() => openDetailModal(user)}
        >
          <td>
            {isPremium && <i className="fa fa-star text-warning me-2" aria-hidden="true" />}
            {highlightMatch(fullName || "-", search.trim())}
          </td>
          <td>{getEmail(user)}</td>
          <td>{user?.phoneNumber || "-"}</td>
          <td onClick={(e) => e.stopPropagation()}>
            <div className="d-flex align-items-center gap-2">
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={Boolean(user?.state)}
                  onChange={(e) => handleToggleUserActive(e, user)}
                  disabled={updateLoading}
                />
              </div>
              <span>{user?.state ? "Aktif" : "Pasif"}</span>
            </div>
          </td>
          <td>
            <span className={`badge ${isDemo ? "badge-info" : isPremiumOnly ? "badge-warning" : "badge-secondary"}`}>
              {isDemo ? "Demo" : isPremiumOnly ? t("admin_common.premium") : t("admin_common.standard")}
            </span>
          </td>
          <td>{formatDate(user?.createdAt)}</td>
        </tr>
      );
    });
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Kullanıcı Yönetimi</h4>
              <div className="mb-3 d-flex gap-2 flex-wrap justify-content-between align-items-center">
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className={`btn ${packageFilter === "all" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => {
                      setPackageFilter("all");
                      setPage(1);
                    }}
                  >
                    {t("admin_common.all")}
                  </button>
                  <button
                    type="button"
                    className={`btn ${packageFilter === "premium" ? "btn-warning" : "btn-outline-warning"}`}
                    onClick={() => {
                      setPackageFilter("premium");
                      setPage(1);
                    }}
                  >
                    {t("admin_common.premium")}
                  </button>
                  <button
                    type="button"
                    className={`btn ${packageFilter === "demo" ? "btn-info" : "btn-outline-info"}`}
                    onClick={() => {
                      setPackageFilter("demo");
                      setPage(1);
                    }}
                  >
                    Demo
                  </button>
                  <button
                    type="button"
                    className={`btn ${packageFilter === "standard" ? "btn-secondary" : "btn-outline-secondary"}`}
                    onClick={() => {
                      setPackageFilter("standard");
                      setPage(1);
                    }}
                  >
                    {t("admin_common.standard")}
                  </button>
                </div>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => {
                    setCreateSuccessMessage("");
                    setShowCreateModal(true);
                  }}
                >
                  Kullanıcı Oluştur
                </button>
              </div>
              {createSuccessMessage && (
                <div className="alert alert-success">{createSuccessMessage}</div>
              )}
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("admin_common.search_name_placeholder")}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>{t("admin_common.name")}</th>
                      <th>{t("admin_common.email")}</th>
                      <th>Telefon</th>
                      <th>{t("admin_common.status")}</th>
                      <th>{t("admin_common.package")}</th>
                      <th>{t("admin_common.created_at")}</th>
                    </tr>
                  </thead>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>
              <div className="d-flex align-items-center justify-content-between mt-3">
                <span className="text-muted">
                  {t("admin_common.total_records", { count: filteredUsers.length })}
                </span>
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    {t("admin_common.prev")}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" disabled>
                    {currentPage} / {totalPages}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t("admin_common.next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showCreateModal && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content" style={{ maxHeight: "90vh" }}>
              <div className="modal-header">
                <h5 className="modal-title">Kullanıcı Oluştur</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateModal(false)}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body" style={{ overflowY: "auto" }}>
                {createError && (
                  <div className="alert alert-danger mb-3">
                    {createMessage || "Kullanıcı oluşturulamadı."}
                  </div>
                )}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Ad</label>
                    <input className="form-control" name="name" value={createForm.name} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Soyad</label>
                    <input className="form-control" name="surName" value={createForm.surName} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">E-posta</label>
                    <input type="email" className="form-control" name="email" value={createForm.email} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Geçici Şifre</label>
                    <input type="password" className="form-control" name="tempPassword" value={createForm.tempPassword} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Telefon</label>
                    <input className="form-control" name="phoneNumber" value={createForm.phoneNumber} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Kullanıcı Adı</label>
                    <input className="form-control" name="userName" value={createForm.userName} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Şirket Adı</label>
                    <input className="form-control" name="businessName" value={createForm.businessName} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Şirket Pozisyonu</label>
                    <input className="form-control" name="businessPosition" value={createForm.businessPosition} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Meslek</label>
                    <input className="form-control" name="job" value={createForm.job} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Şehir</label>
                    <input className="form-control" name="city" value={createForm.city} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Lokasyon</label>
                    <input className="form-control" name="location" value={createForm.location} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Ülke</label>
                    <input className="form-control" name="country" value={createForm.country} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Dil</label>
                    <input className="form-control" name="language" value={createForm.language} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Business ID</label>
                    <input className="form-control" name="businessId" value={createForm.businessId} onChange={handleCreateChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Hakkında</label>
                    <input className="form-control" name="about" value={createForm.about} onChange={handleCreateChange} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setShowCreateModal(false)}>
                  Vazgeç
                </button>
                <button type="button" className="btn btn-success" onClick={handleCreateUser} disabled={createLoading}>
                  {createLoading ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {selectedUser && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content" style={{ maxHeight: "90vh" }}>
              <div className="modal-header">
                <h5 className="modal-title">Kullanıcı Detayı</h5>
                <div className="d-flex align-items-center gap-2">
                  {!isDetailEditMode && !isDemoEditMode && !isPremiumEditMode && selectedUser?.premium === true && selectedUser?.demo === true && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleEndDemo}
                      title="Demoyu Bitir"
                      disabled={updateLoading}
                    >
                      {updateLoading ? "İşleniyor..." : "Demoyu Bitir"}
                    </button>
                  )}
                  {!isDetailEditMode && !isDemoEditMode && !isPremiumEditMode && selectedUser?.premium === true && selectedUser?.demo !== true && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleEndPremium}
                      title="Premium Bitir"
                      disabled={updateLoading}
                    >
                      {updateLoading ? "İşleniyor..." : "Premium Bitir"}
                    </button>
                  )}
                  {!isDetailEditMode && !isDemoEditMode && !isPremiumEditMode && selectedUser?.premium !== true && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success"
                      onClick={() => {
                        setIsDemoEditMode(true);
                        setDetailError("");
                      }}
                      title="Demo"
                    >
                      Demo
                    </button>
                  )}
                  {!isDetailEditMode && !isDemoEditMode && !isPremiumEditMode && selectedUser?.premium !== true && (
                    <button
                      type="button"

                      className="btn btn-sm btn-outline-warning"
                      onClick={() => {
                        setIsPremiumEditMode(true);
                        setDetailError("");
                      }}
                      title="Premium"
                    >
                      Premium
                    </button>
                  )}
                  {!isDetailEditMode ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setIsDetailEditMode(true)}
                      title="Güncelle"
                    >
                      <i className="fa fa-pencil" aria-hidden="true" />
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={() => {
                          setDetailForm(getDetailFormFromUser(selectedUser));
                          setIsDetailEditMode(false);
                          setDetailError("");
                        }}
                        disabled={updateLoading}
                      >
                        Vazgeç
                      </button>
                      <button type="button" className="btn btn-sm btn-success" onClick={handleDetailSave} disabled={updateLoading}>
                        {updateLoading ? "Kaydediliyor..." : "Kaydet"}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedUser(null)}
                    aria-label="Close"
                  />
                </div>
              </div>
              <div className="modal-body" style={{ overflowY: "auto" }}>
                {detailError && (
                  <div className="alert alert-danger mb-3">{detailError}</div>
                )}
                {isDemoEditMode && (
                  <div className="row mb-3">
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Demo Başlangıç</strong></label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={demoForm.demostartdate}
                        onChange={(e) => setDemoForm((prev) => ({ ...prev, demostartdate: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Demo Bitiş</strong></label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={demoForm.demoenddate}
                        onChange={(e) => setDemoForm((prev) => ({ ...prev, demoenddate: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Demo Süresi</strong></label>
                      <input
                        type="text"
                        className="form-control"
                        value={demoForm.demoduration}
                        onChange={(e) => setDemoForm((prev) => ({ ...prev, demoduration: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Toplam Analiz Dakikası</strong></label>
                      <input
                        type="number"
                        className="form-control"
                        value={demoForm.demototalanalysisminutes}
                        onChange={(e) => setDemoForm((prev) => ({ ...prev, demototalanalysisminutes: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Kalan Analiz Süresi</strong></label>
                      <input
                        type="number"
                        className="form-control"
                        value={demoForm.demoremaininganalysisminutes}
                        onChange={(e) => setDemoForm((prev) => ({ ...prev, demoremaininganalysisminutes: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Demo Paketi</strong></label>
                      <input
                        type="text"
                        className="form-control"
                        value={demoForm.demopackage}
                        onChange={(e) => setDemoForm((prev) => ({ ...prev, demopackage: e.target.value }))}
                      />
                    </div>
                    <div className="col-12 d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-light btn-sm"
                        onClick={() => setIsDemoEditMode(false)}
                      >
                        Vazgeç
                      </button>
                      <button
                        type="button"
                        className="btn btn-warning btn-sm"
                        onClick={handleDemoSave}
                        disabled={updateLoading}
                      >
                        {updateLoading ? "Kaydediliyor..." : "Demo Kaydet"}
                      </button>
                    </div>
                  </div>
                )}
                {isPremiumEditMode && (
                  <div className="row mb-3">
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Premium Başlangıç</strong></label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={premiumForm.premiumstartdate}
                        onChange={(e) => setPremiumForm((prev) => ({ ...prev, premiumstartdate: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Premium Bitiş</strong></label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={premiumForm.premiumenddate}
                        onChange={(e) => setPremiumForm((prev) => ({ ...prev, premiumenddate: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Premium Süresi</strong></label>
                      <input
                        type="text"
                        className="form-control"
                        value={premiumForm.premiumduration}
                        onChange={(e) => setPremiumForm((prev) => ({ ...prev, premiumduration: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Toplam Analiz Dakikası</strong></label>
                      <input
                        type="number"
                        className="form-control"
                        value={premiumForm.premiumtotalanalysisminutes}
                        onChange={(e) => setPremiumForm((prev) => ({ ...prev, premiumtotalanalysisminutes: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Kalan Analiz Süresi</strong></label>
                      <input
                        type="number"
                        className="form-control"
                        value={premiumForm.premiumremaininganalysisminutes}
                        onChange={(e) => setPremiumForm((prev) => ({ ...prev, premiumremaininganalysisminutes: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label"><strong>Premium Paketi</strong></label>
                      <input
                        type="text"
                        className="form-control"
                        value={premiumForm.premiumpackage}
                        onChange={(e) => setPremiumForm((prev) => ({ ...prev, premiumpackage: e.target.value }))}
                      />
                    </div>
                    <div className="col-12 d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-light btn-sm"
                        onClick={() => setIsPremiumEditMode(false)}
                      >
                        Vazgeç
                      </button>
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={handlePremiumSave}
                        disabled={updateLoading}
                      >
                        {updateLoading ? "Kaydediliyor..." : "Premium Kaydet"}
                      </button>
                    </div>
                  </div>
                )}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Ad</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="name" value={detailForm.name} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.name)}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Soyad</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="surName" value={detailForm.surName} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.surName)}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>E-posta</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="userEmail" value={detailForm.userEmail} onChange={handleDetailChange} /> : <div>{valueOrDash(getEmail(selectedUser))}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Telefon</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="phoneNumber" value={detailForm.phoneNumber} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.phoneNumber)}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Kullanıcı Adı</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="userName" value={detailForm.userName} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.userName)}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Rol</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="role" value={detailForm.role} onChange={handleDetailChange} /> : <div>{valueOrDash(getRole(selectedUser))}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Durum</strong></label>
                    {isDetailEditMode ? (
                      <select
                        className="form-control"
                        name="state"
                        value={detailForm.state ? "active" : "passive"}
                        onChange={(e) => setDetailForm((prev) => ({ ...prev, state: e.target.value === "active" }))}
                      >
                        <option value="active">Aktif</option>
                        <option value="passive">Pasif</option>
                      </select>
                    ) : (
                      <div>{selectedUser?.state ? t("admin_common.status_active") : t("admin_common.status_passive")}</div>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Paket</strong></label>
                    {isDetailEditMode ? (
                      <select
                        className="form-control"
                        name="premium"
                        value={detailForm.premium ? "premium" : "standard"}
                        onChange={(e) => setDetailForm((prev) => ({ ...prev, premium: e.target.value === "premium" }))}
                      >
                        <option value="standard">Standart</option>
                        <option value="premium">Premium</option>
                      </select>
                    ) : (
                      <div>{isDemoUser(selectedUser) ? "Demo" : isPremiumUser(selectedUser) ? t("admin_common.premium") : t("admin_common.standard")}</div>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Business ID</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="businessId" value={detailForm.businessId} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.businessId)}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Şirket Adı</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="businessName" value={detailForm.businessName} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.businessName)}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Şirket Pozisyonu</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="businessPosition" value={detailForm.businessPosition} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.businessPosition)}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Meslek</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="job" value={detailForm.job} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.job)}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Şehir</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="city" value={detailForm.city} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.city)}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Lokasyon</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="location" value={detailForm.location} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.location)}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Ülke</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="country" value={detailForm.country} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.country)}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Dil</strong></label>
                    {isDetailEditMode ? <input className="form-control" name="language" value={detailForm.language} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.language)}</div>}
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label"><strong>Hakkında</strong></label>
                    {isDetailEditMode ? <textarea className="form-control" rows="3" name="about" value={detailForm.about} onChange={handleDetailChange} /> : <div>{valueOrDash(selectedUser?.about)}</div>}
                  </div>
                  <div className="col-md-6 mb-3"><strong>Kullanıcı ID:</strong> {valueOrDash(selectedUser?.userId || selectedUser?.id)}</div>
                  <div className="col-md-6 mb-3"><strong>Oluşturulma:</strong> {formatDate(selectedUser?.createdAt || selectedUser?.createDate)}</div>
                  <div className="col-md-6 mb-3"><strong>Son Giriş:</strong> {formatDate(selectedUser?.lastLoginDate)}</div>
                  <div className="col-md-6 mb-3"><strong>Fiyatlandırma:</strong> {formatDate(selectedUser?.pricingDate)}</div>
                  {(selectedUser?.demostartdate || selectedUser?.demoenddate || selectedUser?.demopackage || selectedUser?.demoduration || selectedUser?.demototalanalysisminutes || selectedUser?.demoremaininganalysisminutes) && (
                    <>
                      <div className="col-12 mt-2 mb-2">
                        <hr />
                        <div className="d-flex align-items-center justify-content-between">
                          <h6 className="mb-0"><strong>Demo Bilgileri</strong></h6>
                          {isDemoExpired(selectedUser) ? (
                            <span className="badge badge-danger" style={{ fontSize: "0.85rem" }}>Demo Sonlandı</span>
                          ) : (
                            <span className="badge badge-success" style={{ fontSize: "0.85rem" }}>Demo Devam Ediyor</span>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6 mb-3"><strong>Demo Başlangıç:</strong> {formatDate(selectedUser?.demostartdate)}</div>
                      <div className="col-md-6 mb-3"><strong>Demo Bitiş:</strong> {formatDate(selectedUser?.demoenddate)}</div>
                      <div className="col-md-6 mb-3"><strong>Demo Süresi:</strong> {valueOrDash(selectedUser?.demoduration)}</div>
                      <div className="col-md-6 mb-3"><strong>Demo Paketi:</strong> {valueOrDash(selectedUser?.demopackage)}</div>
                      <div className="col-md-6 mb-3"><strong>Toplam Analiz Dakikası:</strong> {valueOrDash(selectedUser?.demototalanalysisminutes)}</div>
                      <div className="col-md-6 mb-3"><strong>Kalan Analiz Süresi:</strong> {valueOrDash(selectedUser?.demoremaininganalysisminutes)}</div>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setSelectedUser(null)}>
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UsersListPage;
